import Icon from '@ant-design/icons';
import styled from '@emotion/styled';
import { useRequest } from 'ahooks';
import { Button, Popover, List, Popconfirm, Space, TableColumnsType } from 'antd';
import QuickRangePicker, { QuickAction } from 'components/QuickRangePicker';
import dayjs from 'lib/dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { BlockListItem, removeFromBlockList } from 'services/api/blockList';
import { getStoreListByIds } from 'services/api/store';
import ScrollTable from 'components/ScrollTable';
import { ReactComponent as iconMore } from 'views/Setting/BlockList/assets/more.svg';
import { ReactComponent as iconBaned } from 'views/Setting/BlockList/assets/ban.svg';
import { ReactComponent as regionSvg } from 'views/Setting/BlockList/assets/region.svg';
import BlockListSettingModal from './SettingModal';
import { useGetRegionLabel } from 'hooks/business/useRegion';
import { useGetMetaReason } from './useOfficialReason';

export const Container = styled.div`
  background-color: var(--fett-bg-panel);
  border-radius: 4px;
  margin-top: 16px;
  flex: 1;
  overflow: hidden;
  flex-direction: column;
  display: flex;
  header {
    height: 56px;
    padding: 12px 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex: none;
    border: 0;
    border-bottom: 1px solid var(--fett-border-default);
    h2 {
      font-size: 16px;
      color: var(--fett-text-primary);
      font-weight: 500;
      margin: 0;
    }
  }
  main {
    padding: 24px;
    flex: 1;
    overflow: hidden;
  }
`;
const StoreListPopoverContent = styled(List)`
  max-height: 420px;
  .ant-list-item {
    width: 240px;
    overflow: hidden;
    display: flex;
    div {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      flex: 1;
    }
    svg {
      flex: none;
      margin-left: 8px;
      color: var(--fett-text-tertiary);
    }
  }
`;

interface Props {
  dateRange?: [dayjs.Dayjs | null, dayjs.Dayjs | null];
  onDateRangeChange: (value?: [dayjs.Dayjs | null, dayjs.Dayjs | null]) => void;
  order: 'ascend' | 'descend';
  onOrderChange: (value: 'ascend' | 'descend') => void;
  pagination: {
    pageNo: number;
    pageSize: number;
  };
  onPageChange: (value: { pageNo: number; pageSize: number }) => void;
  total: number;
  listData: BlockListItem[];
  loading: boolean;
  refresh: () => void;
}

const StoreListPopover = ({ storeIds }: { storeIds: number[] }) => {
  const { t } = useTranslate();
  const [visible, setVisible] = useState(false);
  const {
    run,
    data: storeList = [],
    loading,
  } = useRequest(
    async () => {
      const ret = await getStoreListByIds(storeIds);
      return ret;
    },
    {
      manual: true,
    },
  );
  useEffect(() => {
    if (storeIds.length === 1) {
      run();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeIds.length]);
  if (storeIds.length === 1) {
    return <>{storeList?.[0]?.storeName}</>;
  }
  return (
    <Popover
      visible={visible}
      onVisibleChange={() => setVisible(false)}
      content={
        <StoreListPopoverContent loading={loading}>
          {storeList.map((store) => (
            <List.Item key={store.storeId}>
              <div>{store.storeName}</div>
              <Icon component={iconBaned} />
            </List.Item>
          ))}
        </StoreListPopoverContent>
      }
    >
      <Button
        type="link"
        onClick={() => {
          run();
          setVisible(true);
        }}
      >
        {t('ViewBlockList.{x}家店铺', { count: storeIds.length })}
        <Icon component={iconMore} />
      </Button>
    </Popover>
  );
};

const BlockListTable = React.memo((props: Props) => {
  const {
    dateRange,
    onDateRangeChange,
    order,
    onOrderChange,
    pagination,
    onPageChange,
    total,
    listData,
    loading,
    refresh,
  } = props;
  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();
  const [settingModalVisible, setSettingModalVisible] = useState(
    localStorage.getItem('fett/blockListInitialized') !== 'initialized',
  );
  const quickActions: QuickAction[] = useMemo(() => {
    return [
      {
        key: 'yesterday',
        label: t('ViewBlockList.table.quickDateRange.yesterday') as unknown as string,
        range: () => {
          return [
            dayjs().subtract(1, 'day').startOf('day'),
            dayjs().subtract(1, 'day').startOf('day'),
          ];
        },
      },
      {
        key: 'today',
        label: t('ViewBlockList.table.quickDateRange.today') as unknown as string,
        range: () => {
          return [
            dayjs().subtract(0, 'day').startOf('day'),
            dayjs().subtract(0, 'day').startOf('day'),
          ];
        },
      },
      {
        key: 'last7days',
        label: t('ViewBlockList.table.quickDateRange.last7days') as unknown as string,
        range: () => {
          return [
            dayjs().subtract(7, 'day').startOf('day'),
            dayjs().subtract(1, 'day').startOf('day'),
          ];
        },
      },
    ];
  }, [t]);
  const getReason = useGetMetaReason();

  const columns = useMemo<TableColumnsType<any>>(() => {
    return [
      {
        dataIndex: 'buyerName',
        title: t('ViewBlockList.表格列买家昵称'),
        width: 200,
        fixed: 'left',
      },
      {
        dataIndex: 'reason',
        title: t('ViewBlockList.表格列拉黑原因'),
        width: 200,
        render: (cell: string[], record: Record<string, any>) => {
          return getReason(cell.toString());
        },
      },
      {
        dataIndex: 'remark',
        title: t('ViewBlockList.表格列拉黑备注'),
        width: 200,
      },
      {
        dataIndex: 'blacklistScope',
        title: t('ViewBlockList.表格列拉黑范围'),
        width: 200,
        render: (_: any, record: BlockListItem) => {
          const { blacklistScope, storeIds } = record;
          if (blacklistScope === 1) {
            return <Space>{t('ViewBlockList.全部店铺')}</Space>;
          }
          return <StoreListPopover storeIds={storeIds} />;
        },
      },
      {
        dataIndex: 'region',
        title: t('ViewBlockList.表格列所属站点'),
        width: 150,
        render: (cell: General.UpperCaseRegion) => {
          return getRegionLabel(cell);
        },
      },
      {
        dataIndex: 'operationUserName',
        title: t('voucher.操作人'),
        width: 150,
      },
      {
        dataIndex: 'operationTime',
        title: t('ViewBlockList.表格列操作时间'),
        width: 200,
        sorter: true,
        showSorterTooltip: false,
        sortOrder: order,
        sortDirections: ['descend', 'ascend'] as any,
        render: (cell: string) => {
          return dayjs(Number(cell)).format('YYYY-MM-DD HH:mm:ss');
        },
      },
      {
        dataIndex: 'action',
        title: t('操作'),
        width: 250,
        fixed: 'right',
        render: (cell: any, record: BlockListItem) => {
          return (
            <Popconfirm
              onConfirm={() => {
                removeFromBlockList({
                  id: record.id,
                  blacklistScope: 1,
                  storeId: '',
                }).then(() => {
                  refresh();
                });
              }}
              title={t('ViewBlockList.你确认要将买家移除黑名单吗')}
            >
              <Button type="link">{t('ViewBlockList.取消拉黑')}</Button>
            </Popconfirm>
          );
        },
      },
    ];
  }, [t, order, refresh, getRegionLabel, getReason]);
  const data = useMemo(() => {
    return listData.map((row) => {
      return {
        ...row,
        remark: row.remark || '-',
      };
    });
  }, [listData]);
  return (
    <Container>
      <header>
        <h2>{t('ViewBlockList.table.label')}</h2>
        <Space>
          <QuickRangePicker
            value={dateRange}
            onChange={(values) => {
              if (!values) {
                onDateRangeChange();
              } else {
                onDateRangeChange(values);
              }
            }}
            quickActions={quickActions}
            disabledDate={(date) => date.isAfter(dayjs(), 'days')}
          />{' '}
          <Button
            icon={<Icon component={regionSvg} />}
            onClick={() => setSettingModalVisible(true)}
          >
            {t('ViewBlockList.setting')}
          </Button>
        </Space>
      </header>
      <main>
        <ScrollTable
          columns={columns}
          dataSource={data}
          loading={loading}
          onChange={(pagination, filters, sorter) => {
            onOrderChange((sorter as any).order || 'descend');
          }}
          rowKey="id"
          pagination={{
            showQuickJumper: true,
            total,
            current: pagination.pageNo,
            pageSize: pagination.pageSize,
            showTotal: (total) => t('feedback.共x项', { x: total }),
            onChange: (page: number, pageSize: number) =>
              onPageChange({
                pageNo: page,
                pageSize,
              }),
          }}
        />
      </main>
      <BlockListSettingModal
        visible={settingModalVisible}
        onClose={() => {
          setSettingModalVisible(false);
          localStorage.setItem('fett/blockListInitialized', 'initialized');
        }}
      />
    </Container>
  );
});

export default BlockListTable;
